// Utility functions for role-based access control
import { User, UserManagementUser, OrganizationRole, SystemRole } from '../types';

/**
 * Helper function to get the effective role of a user
 * This handles both new and legacy role structures
 */
export function getEffectiveRole(user: User | UserManagementUser | null | undefined): {
  systemRole: SystemRole;
  organizationRole?: OrganizationRole;
  organizationId?: string;
} {
  if (!user) {
    return { systemRole: 'user' as SystemRole };
  }

  // If using new structure
  if (user.role === 'super_admin' || user.role === 'user') {
    // Super admins should not have organizationRole or organizationId
    if (user.role === 'super_admin') {
      return {
        systemRole: 'super_admin',
        organizationRole: undefined,
        organizationId: undefined
      };
    }
    return {
      systemRole: user.role,
      organizationRole: user.organizationRole,
      organizationId: user.organizationId
    };
  }

  // Legacy structure - convert old roles to new format
  // This allows backward compatibility during migration
  const legacyRole = user.legacyRole || (user.role as any);
  
  if (legacyRole === 'super_admin') {
    return { systemRole: 'super_admin' };
  }
  
  // All other legacy roles become 'user' with appropriate organizationRole
  return {
    systemRole: 'user',
    organizationRole: legacyRole as OrganizationRole,
    organizationId: user.organizationId
  };
}

/**
 * Check if user is a super admin
 */
export function isSuperAdmin(user: User | UserManagementUser | null | undefined): boolean {
  const { systemRole } = getEffectiveRole(user);
  return systemRole === 'super_admin';
}

/**
 * Check if user is an organization admin
 */
export function isOrgAdmin(user: User | UserManagementUser | null | undefined): boolean {
  const { organizationRole } = getEffectiveRole(user);
  return organizationRole === 'admin';
}

/**
 * Check if user has a specific organization role
 */
export function hasOrgRole(
  user: User | UserManagementUser | null | undefined, 
  role: OrganizationRole
): boolean {
  const { organizationRole } = getEffectiveRole(user);
  return organizationRole === role;
}

/**
 * Check if user has any of the specified organization roles
 */
export function hasAnyOrgRole(
  user: User | UserManagementUser | null | undefined,
  roles: OrganizationRole[]
): boolean {
  const { organizationRole } = getEffectiveRole(user);
  return organizationRole ? roles.includes(organizationRole) : false;
}

/**
 * Check if user belongs to a specific organization
 */
export function belongsToOrganization(
  user: User | UserManagementUser | null | undefined,
  organizationId: string
): boolean {
  const { organizationId: userOrgId } = getEffectiveRole(user);
  return userOrgId === organizationId;
}

/**
 * Check if user can manage users (super_admin or org admin)
 */
export function canManageUsers(user: User | UserManagementUser | null | undefined): boolean {
  return isSuperAdmin(user) || isOrgAdmin(user);
}

/**
 * Check if user can see the Users tab (super_admin only)
 */
export function canSeeUsersTab(user: User | UserManagementUser | null | undefined): boolean {
  return isSuperAdmin(user);
}

/**
 * Check if user can see the Organizations tab (super_admin only)
 */
export function canSeeOrganizationsTab(user: User | UserManagementUser | null | undefined): boolean {
  return isSuperAdmin(user);
}

/**
 * Check if user can see the Forms tab (all users can access)
 */
export function canSeeFormsTab(user: User | UserManagementUser | null | undefined): boolean {
  return true; // All users can access Forms tab
}

/**
 * Check if user can see the Programs tab
 */
export function canSeeProgramsTab(user: User | UserManagementUser | null | undefined): boolean {
  if (isSuperAdmin(user)) return true;
  return hasAnyOrgRole(user, ['admin', 'department_manager']);
}

/**
 * Check if user can see the Groups tab
 */
export function canSeeGroupsTab(user: User | UserManagementUser | null | undefined): boolean {
  if (isSuperAdmin(user)) return true;
  return hasAnyOrgRole(user, ['admin', 'program_manager', 'team_manager']);
}

/**
 * Check if user can see the Members tab
 */
export function canSeeMembersTab(user: User | UserManagementUser | null | undefined): boolean {
  if (isSuperAdmin(user)) return true;
  return hasAnyOrgRole(user, ['admin', 'department_manager', 'program_manager', 'team_manager']);
}

/**
 * Check if user can see the sitemap (geographic distribution)
 * Only Program Managers and Department Managers can see it
 */
export function canSeeSitemap(user: User | UserManagementUser | null | undefined): boolean {
  if (!user) return false;
  const { systemRole, organizationRole } = getEffectiveRole(user);
  // Super admin can see everything
  if (systemRole === 'super_admin') return true;
  // Only program_manager and department_manager can see sitemap
  return organizationRole === 'program_manager' || organizationRole === 'department_manager';
}

/**
 * Get display name for an organization role
 */
export function getOrgRoleDisplayName(role: OrganizationRole, t: (key: string) => string): string {
  const roleMap: Record<OrganizationRole, string> = {
    admin: t('users.roles.admin'),
    department_manager: t('users.roles.department_manager'),
    program_manager: t('users.roles.program_manager'),
    team_manager: t('users.roles.team_manager'),
    instructor: t('users.roles.instructor'),
    social_worker: t('users.roles.social_worker'),
    blocked: t('users.roles.blocked')
  };
  return roleMap[role] || role;
}

/**
 * Get display name for system role + org role combination
 */
export function getUserRoleDisplayName(user: User | UserManagementUser | null | undefined, t: (key: string) => string): string {
  const { systemRole, organizationRole } = getEffectiveRole(user);
  
  if (systemRole === 'super_admin') {
    return t('users.roles.super_admin');
  }
  
  if (organizationRole) {
    return getOrgRoleDisplayName(organizationRole, t);
  }
  
  return t('users.roles.user') || 'User';
}

/**
 * Check if user can view a specific patient based on role hierarchy
 * - Mentor/Instructor: Only patients assigned to them
 * - Social Worker: Only patients assigned to them
 * - Team Manager: Patients in their team (assigned to them or their team members)
 * - Program Manager: All patients in their program
 * - Department Manager: All patients in all programs
 */
export function canViewPatient(
  user: User | UserManagementUser | null | undefined,
  patient: any,
  userPrograms?: string[],
  userGroups?: string[]
): boolean {
  if (!user || !patient) return false;
  
  const { systemRole, organizationRole } = getEffectiveRole(user);
  
  // Super admin can see everything
  if (systemRole === 'super_admin') return true;
  
  // Check if user is assigned to this patient
  // Note: For social_worker and instructor, only check assignedAdmins, not createdBy
  const isAssigned = Array.isArray(patient.assignedAdmins) && patient.assignedAdmins.includes(user.id);
  
  // Role-based access
  switch (organizationRole) {
    case 'department_manager':
      // Can see all patients in their organization
      // If both have organizationId, they must match
      if (patient.organizationId && user.organizationId) {
        return patient.organizationId === user.organizationId;
      }
      // For backward compatibility: if patient has no organizationId but user does,
      // department manager can see patients assigned to them or created by them
      if (!patient.organizationId && user.organizationId) {
        return isAssigned || patient.createdBy === user.id;
      }
      // If neither has organizationId (shouldn't happen but handle it)
      return isAssigned;
    
    case 'program_manager':
      // Can see all patients in their programs
      if (userPrograms && userPrograms.length > 0 && patient.programId) {
        return userPrograms.includes(patient.programId);
      }
      // Fallback to organization level - must match organizationId
      // If patient has no organizationId, they cannot access it
      return patient.organizationId === user.organizationId;
    
    case 'team_manager':
      // Can see patients in their groups/teams
      if (userGroups && userGroups.length > 0 && patient.groupId) {
        return userGroups.includes(patient.groupId);
      }
      // Or patients assigned to them
      return isAssigned;
    
    case 'social_worker':
    case 'instructor':
      // Can only see patients assigned to them
      return isAssigned;
    
    default:
      return isAssigned;
  }
}

/**
 * Check if user can create new patients
 * Only Program Managers and Department Managers can create patients
 */
export function canCreatePatient(user: User | UserManagementUser | null | undefined): boolean {
  if (!user) return false;
  
  const { systemRole, organizationRole } = getEffectiveRole(user);
  
  // Super admin can create
  if (systemRole === 'super_admin') return true;
  
  // Only program_manager and department_manager can create patients
  return organizationRole === 'program_manager' || organizationRole === 'department_manager';
}

/**
 * Check if user can delete a patient
 * Only Program Managers and Department Managers can delete patients
 */
export function canDeletePatient(user: User | UserManagementUser | null | undefined): boolean {
  if (!user) return false;
  
  const { systemRole, organizationRole } = getEffectiveRole(user);
  
  // Super admin can delete
  if (systemRole === 'super_admin') return true;
  
  // Only program_manager and department_manager can delete patients
  return organizationRole === 'program_manager' || organizationRole === 'department_manager';
}

/**
 * Check if user can assign workers to a patient
 * Only Program Managers and Department Managers can assign workers
 * Note: This is for adding NEW workers, not just assigning existing ones
 */
export function canAssignWorkers(user: User | UserManagementUser | null | undefined): boolean {
  if (!user) return false;
  
  const { systemRole, organizationRole } = getEffectiveRole(user);
  
  // Super admin can assign
  if (systemRole === 'super_admin') return true;
  
  // Only program_manager and department_manager can add new workers
  return organizationRole === 'program_manager' || organizationRole === 'department_manager';
}

/**
 * Check if user can edit patient information
 * All roles can edit patients they have access to
 */
export function canEditPatient(
  user: User | UserManagementUser | null | undefined,
  patient: any,
  userPrograms?: string[],
  userGroups?: string[]
): boolean {
  // If you can view it, you can edit it
  return canViewPatient(user, patient, userPrograms, userGroups);
}

/**
 * Check if user can add meeting documentation
 * All roles can add meetings to patients they can view
 */
export function canAddMeeting(
  user: User | UserManagementUser | null | undefined,
  patient: any,
  userPrograms?: string[],
  userGroups?: string[]
): boolean {
  // If you can view the patient, you can add meetings
  return canViewPatient(user, patient, userPrograms, userGroups);
}

/**
 * Check if user can edit a specific meeting note
 * Only the user who created the meeting can edit it (except super admin)
 */
export function canEditMeeting(
  user: User | UserManagementUser | null | undefined,
  meeting: any
): boolean {
  if (!user || !meeting) return false;
  
  const { systemRole } = getEffectiveRole(user);
  
  // Super admin can edit everything
  if (systemRole === 'super_admin') return true;
  
  // Only the user who created the meeting can edit it
  const isOwner = meeting.createdBy === user.email || 
                  meeting.userEmail === user.email ||
                  meeting.createdBy === user.id;
  
  return isOwner;
}

/**
 * Check if user can delete a meeting note
 * Only the creator or managers can delete meetings
 */
export function canDeleteMeeting(
  user: User | UserManagementUser | null | undefined,
  meeting: any
): boolean {
  if (!user || !meeting) return false;
  
  const { systemRole, organizationRole } = getEffectiveRole(user);
  
  // Super admin can delete everything
  if (systemRole === 'super_admin') return true;
  
  // Check if user created this meeting
  const isOwner = meeting.createdBy === user.email || 
                  meeting.userEmail === user.email ||
                  meeting.createdBy === user.id;
  
  switch (organizationRole) {
    case 'instructor':
    case 'social_worker':
      // Can only delete their own meetings
      return isOwner;
    
    case 'team_manager':
    case 'program_manager':
    case 'department_manager':
      // Managers can delete any meeting
      return true;
    
    default:
      return false;
  }
}

/**
 * Check if user can add comments to meetings
 * Only managers (team_manager, program_manager, department_manager) and super admin can comment
 */
export function canCommentOnMeeting(
  user: User | UserManagementUser | null | undefined
): boolean {
  if (!user) return false;
  
  const { systemRole, organizationRole } = getEffectiveRole(user);
  
  // Super admin can comment
  if (systemRole === 'super_admin') return true;
  
  // Only managers can comment
  return organizationRole === 'team_manager' || 
         organizationRole === 'program_manager' || 
         organizationRole === 'department_manager';
}

/**
 * Check if user can create/add milestones
 * - Instructor/Mentor: Cannot create milestones
 * - Social Worker: Can create milestones
 * - All Managers: Can create milestones
 */
export function canAddMilestone(
  user: User | UserManagementUser | null | undefined,
  patient: any,
  userPrograms?: string[],
  userGroups?: string[]
): boolean {
  if (!user || !patient) return false;
  
  const { systemRole, organizationRole } = getEffectiveRole(user);
  
  // Super admin can do everything
  if (systemRole === 'super_admin') return true;
  
  // Instructors cannot add milestones
  if (organizationRole === 'instructor') return false;
  
  // Everyone else who can view the patient can add milestones
  return canViewPatient(user, patient, userPrograms, userGroups);
}

/**
 * Check if user can edit a milestone
 * - Instructor/Mentor: Cannot edit milestones
 * - Social Worker: Can edit milestones for patients they can view
 * - All Managers: Can edit milestones for patients they can view
 */
export function canEditMilestone(
  user: User | UserManagementUser | null | undefined,
  patient: any,
  userPrograms?: string[],
  userGroups?: string[]
): boolean {
  if (!user || !patient) return false;
  
  const { systemRole, organizationRole } = getEffectiveRole(user);
  
  // Super admin can do everything
  if (systemRole === 'super_admin') return true;
  
  // Instructors cannot edit milestones
  if (organizationRole === 'instructor') return false;
  
  // Everyone else who can view the patient can edit milestones
  return canViewPatient(user, patient, userPrograms, userGroups);
}

/**
 * Check if user can delete a milestone
 * - Instructor/Mentor: Cannot delete milestones
 * - Social Worker: Can delete milestones for patients they can view
 * - All Managers: Can delete milestones for patients they can view
 */
export function canDeleteMilestone(
  user: User | UserManagementUser | null | undefined,
  patient: any,
  userPrograms?: string[],
  userGroups?: string[]
): boolean {
  if (!user || !patient) return false;
  
  const { systemRole, organizationRole } = getEffectiveRole(user);
  
  // Super admin can do everything
  if (systemRole === 'super_admin') return true;
  
  // Instructors cannot delete milestones
  if (organizationRole === 'instructor') return false;
  
  // Everyone else who can view the patient can delete milestones
  return canViewPatient(user, patient, userPrograms, userGroups);
}

/**
 * Check if user can upload/manage documents
 * - All roles who can view the patient can manage documents
 */
export function canManageDocuments(
  user: User | UserManagementUser | null | undefined,
  patient: any,
  userPrograms?: string[],
  userGroups?: string[]
): boolean {
  if (!user || !patient) return false;
  
  const { systemRole, organizationRole } = getEffectiveRole(user);
  
  // Super admin can do everything
  if (systemRole === 'super_admin') return true;
  
  // Instructors cannot manage documents at all
  if (organizationRole === 'instructor') return false;
  
  // Everyone else who can view the patient can manage documents
  return canViewPatient(user, patient, userPrograms, userGroups);
}

/**
 * Check if user can approve action plans (milestones)
 * - Instructor/Mentor: Cannot approve action plans
 * - All other roles: Can approve action plans for patients they can view
 */
export function canApproveActionPlan(
  user: User | UserManagementUser | null | undefined,
  patient: any,
  userPrograms?: string[],
  userGroups?: string[]
): boolean {
  if (!user || !patient) return false;
  
  const { systemRole, organizationRole } = getEffectiveRole(user);
  
  // Super admin can do everything
  if (systemRole === 'super_admin') return true;
  
  // Instructors cannot approve action plans
  if (organizationRole === 'instructor') return false;
  
  // Everyone else who can view the patient can approve action plans
  return canViewPatient(user, patient, userPrograms, userGroups);
}

/**
 * Check if user can return action plan and intake for correction
 * - Instructor/Mentor: Cannot return for correction
 * - Social Worker: Cannot return for correction
 * - Team Manager+: Can return for correction
 */
export function canReturnForCorrection(
  user: User | UserManagementUser | null | undefined,
  patient: any,
  userPrograms?: string[],
  userGroups?: string[]
): boolean {
  if (!user || !patient) return false;
  
  const { systemRole } = getEffectiveRole(user);
  
  // Super admin can do everything
  if (systemRole === 'super_admin') return true;
  
  // Only team_manager and above can return for correction
  if (!hasAnyOrgRole(user, ['team_manager', 'program_manager', 'department_manager'])) {
    return false;
  }
  
  // Must also be able to view the patient
  return canViewPatient(user, patient, userPrograms, userGroups);
}

/**
 * Check if user can generate reports
 * - Instructor/Mentor: Cannot generate reports
 * - Social Worker: Cannot generate reports
 * - Team Manager: Can generate reports for their team only
 * - Program Manager+: Can generate reports
 */
export function canGenerateReports(
  user: User | UserManagementUser | null | undefined,
  scope?: 'team' | 'program' | 'organization'
): boolean {
  if (!user) return false;
  
  const { systemRole, organizationRole } = getEffectiveRole(user);
  
  // Super admin can generate all reports
  if (systemRole === 'super_admin') return true;
  
  switch (organizationRole) {
    case 'instructor':
    case 'social_worker':
      // Cannot generate reports
      return false;
    
    case 'team_manager':
      // Can only generate team reports
      return !scope || scope === 'team';
    
    case 'program_manager':
      // Can generate team and program reports
      return !scope || scope === 'team' || scope === 'program';
    
    case 'department_manager':
      // Can generate all reports
      return true;
    
    default:
      return false;
  }
}

/**
 * Check if user can see note history (who wrote/edited/deleted/added notes)
 * - Instructor/Mentor: Only notes they received
 * - Social Worker: Only on their clients
 * - Team Manager+: Full history
 */
export function canSeeNoteHistory(
  user: User | UserManagementUser | null | undefined,
  patient: any,
  userPrograms?: string[],
  userGroups?: string[]
): boolean {
  if (!user || !patient) return false;
  
  const { systemRole, organizationRole } = getEffectiveRole(user);
  
  // Super admin can see everything
  if (systemRole === 'super_admin') return true;
  
  // Must be able to view the patient first
  if (!canViewPatient(user, patient, userPrograms, userGroups)) {
    return false;
  }
  
  switch (organizationRole) {
    case 'instructor':
      // Can only see notes they received (limited history)
      // This should be implemented in the UI to filter notes
      return true; // Basic access, filtering happens in UI
    
    case 'social_worker':
      // Can only see history on their assigned clients
      return true; // Basic access, filtering happens in UI
    
    case 'team_manager':
    case 'program_manager':
    case 'department_manager':
      // Can see full history
      return true;
    
    default:
      return false;
  }
}

/**
 * Check if user can lock intake forms and reports
 * - Only Program Managers and Department Managers can lock
 * - Once locked, Team Manager, Social Worker, and Mentor cannot edit
 */
export function canLockIntakeAndReports(
  user: User | UserManagementUser | null | undefined
): boolean {
  if (!user) return false;
  
  const { systemRole, organizationRole } = getEffectiveRole(user);
  
  // Super admin can lock
  if (systemRole === 'super_admin') return true;
  
  // Only program_manager and department_manager can lock
  return organizationRole === 'program_manager' || organizationRole === 'department_manager';
}

/**
 * Check if user can edit locked content
 * - If content is locked, only the locker (Program/Department Manager) or super admin can edit
 */
export function canEditLockedContent(
  user: User | UserManagementUser | null | undefined,
  isLocked: boolean
): boolean {
  if (!user) return false;
  
  // If not locked, use regular permissions
  if (!isLocked) return true;
  
  const { systemRole, organizationRole } = getEffectiveRole(user);
  
  // Super admin can edit locked content
  if (systemRole === 'super_admin') return true;
  
  // Only program_manager and department_manager can edit locked content
  return organizationRole === 'program_manager' || organizationRole === 'department_manager';
}


